import type { KeyValue, PsetGroup } from '@viewer/lib/ifcItemData'

/** Os types de um modelo, arrumados para se lerem.
 *
 *  Por classe (IfcColumnType), e dentro de cada classe por nome. Por
 *  nome e não por objecto, porque há exportadores que escrevem um type
 *  por instância: o Revit, no modelo de exemplo, escreve 21
 *  IfcColumnType para 21 pilares, com o nome repetido. Listá-los um a um
 *  escondia isso; agrupados, vê-se logo que são 21 types iguais em vez
 *  de um partilhado, que é o que o IFC pede. */

export interface InstanciaDeType {
  localId: number
  categoria: string
  nome: string
}

export interface TypeLido {
  localId: number
  categoria: string
  nome: string
  atributos: KeyValue[]
  psets: PsetGroup[]
  instancias: InstanciaDeType[]
}

export interface GrupoDeType {
  chave: string
  categoria: string
  nome: string
  types: TypeLido[]
  instancias: InstanciaDeType[]
  /** Mais de um objecto type com o mesmo nome. */
  repetido: boolean
  /** Entre os repetidos, os property sets não são todos iguais: não são
   *  simples cópias, e fundi-los perdia informação. */
  psetsDiferem: boolean
}

export interface ClasseDeTypes {
  categoria: string
  grupos: GrupoDeType[]
  types: number
  instancias: number
}

/** Uma impressão dos property sets, para comparar types entre si. */
export function assinaturaDosPsets(psets: PsetGroup[]): string {
  return psets
    .map((g) => `${g.name}{${g.rows.map((r) => `${r.key}=${r.value}`).sort().join(';')}}`)
    .sort()
    .join('|')
}

export function agruparTypes(types: TypeLido[]): ClasseDeTypes[] {
  const porClasse = new Map<string, Map<string, TypeLido[]>>()
  for (const t of types) {
    const classe = porClasse.get(t.categoria) ?? new Map<string, TypeLido[]>()
    const lista = classe.get(t.nome) ?? []
    lista.push(t)
    classe.set(t.nome, lista)
    porClasse.set(t.categoria, classe)
  }
  const classes: ClasseDeTypes[] = []
  for (const [categoria, porNome] of porClasse) {
    const grupos: GrupoDeType[] = []
    for (const [nome, lista] of porNome) {
      const assinaturas = new Set(lista.map((t) => assinaturaDosPsets(t.psets)))
      grupos.push({
        chave: `${categoria}|${nome}`,
        categoria,
        nome,
        types: lista,
        instancias: lista.flatMap((t) => t.instancias),
        repetido: lista.length > 1,
        psetsDiferem: assinaturas.size > 1,
      })
    }
    grupos.sort((a, b) => a.nome.localeCompare(b.nome, 'pt'))
    classes.push({
      categoria,
      grupos,
      types: grupos.reduce((s, g) => s + g.types.length, 0),
      instancias: grupos.reduce((s, g) => s + g.instancias.length, 0),
    })
  }
  return classes.sort((a, b) => a.categoria.localeCompare(b.categoria))
}

/** Os elementos que podiam ter type e não têm, por classe. */
export function semType(
  elementosPorClasse: Map<string, number[]>,
  comType: Set<number>,
  podeTerType: (categoria: string) => boolean,
): Array<{ categoria: string; ids: number[] }> {
  const out: Array<{ categoria: string; ids: number[] }> = []
  for (const [categoria, ids] of elementosPorClasse) {
    if (!podeTerType(categoria)) continue
    const sem = ids.filter((id) => !comType.has(id))
    if (sem.length > 0) out.push({ categoria, ids: sem })
  }
  return out.sort((a, b) => b.ids.length - a.ids.length)
}

/** Filtrar por texto: no nome do type, na classe, ou no nome de uma
 *  instância. */
export function filtrarTypes(classes: ClasseDeTypes[], texto: string): ClasseDeTypes[] {
  const t = texto.trim().toLowerCase()
  if (!t) return classes
  const out: ClasseDeTypes[] = []
  for (const c of classes) {
    const classeBate = c.categoria.toLowerCase().includes(t)
    const grupos = classeBate
      ? c.grupos
      : c.grupos.filter(
          (g) => g.nome.toLowerCase().includes(t) || g.instancias.some((i) => i.nome.toLowerCase().includes(t)),
        )
    if (grupos.length > 0) {
      out.push({
        ...c,
        grupos,
        types: grupos.reduce((s, g) => s + g.types.length, 0),
        instancias: grupos.reduce((s, g) => s + g.instancias.length, 0),
      })
    }
  }
  return out
}
